import { authApi } from "@/api/auth.api";
import router from "@/router";
import { ApiError, type LoginRequest, type TwoFactorSetup, type User } from "@/types/AuthModels";
import { defineStore } from "pinia";
import { ref } from "vue";

/**
 * Store containing all authentication functions
 */
export const useAuthStore = defineStore(
	"auth",
	() => {
		const user = ref<User | null>(null);
		const error = ref<string | null>(null);
		const isLoading = ref(false);
		const is2faPending = ref(false);
		const isAuthenticated = ref(false);

		/**
		 * Authenticates a user.
		 * Proceed to multi factor authentication or complete authentication accordingly.
		 *
		 * @param credentials
		 */
		async function login(credentials: LoginRequest) {
			isLoading.value = true;
			error.value = null;

			try {
				const response = await authApi.login(credentials);

				if (response) {
					if (response.is2faEnabled) {
						is2faPending.value = true;
						user.value = null;

						return true;
					}

					user.value = response;
					isAuthenticated.value = true;
					is2faPending.value = false;
					return true;
				}

				return false;
			} catch (err: unknown) {
				if (err instanceof ApiError) {
					error.value = err.message || "An unexpected error occured.";
				}

				user.value = null;
				isAuthenticated.value = false;

				return false;
			} finally {
				isLoading.value = false;
			}
		}

		/**
		 * Fully authenticate the user.
		 * @param code one time passcode valid for 30 seconds.
		 */
		async function verify2fa(code: string) {
			isLoading.value = true;
			error.value = null;

			try {
				const response = await authApi.verify2fa(code);

				if (response) {
					user.value = response;
					isAuthenticated.value = true;
					is2faPending.value = false;
					return true;
				}
			} catch (err: unknown) {
				if (err instanceof ApiError) {
					error.value = err.message || "An unexpected error occured.";
				}

				user.value = null;
				isAuthenticated.value = false;
				is2faPending.value = true;

				return false;
			} finally {
				isLoading.value = false;
			}
		}

		async function verify2faAndEnable(code: string) {
			isLoading.value = true;
			error.value = null;

			try {
				const response = await authApi.confirm2fa(code);

				if (response && user.value) {
					user.value.is2faEnabled = true;
					return true;
				}
			} catch (err: unknown) {
				if (err instanceof ApiError) {
					error.value = err.message || "An unexpected error occured.";
				}

				return false;
			} finally {
				isLoading.value = false;
			}
		}

		async function setupTwoFactor(): Promise<TwoFactorSetup | undefined> {
			isLoading.value = true;
			error.value = null;

			try {
				const data = await authApi.setup2fa();

				return data;
			} catch (err: unknown) {
				if (err instanceof ApiError) {
					error.value = err.message || "An unexpected error occured.";
				}
			} finally {
				isLoading.value = false;
			}

			return undefined;
		}

		async function logout() {
			isLoading.value = true;
			error.value = null;

			try {
				await authApi.logout();
			} catch {
				// Silent fail - logout anyways
				router.push({ name: "login" });
			} finally {
				clearUserValues();
			}
		}

		function clearUserValues() {
			user.value = null;
			is2faPending.value = false;
			isLoading.value = false;
			isAuthenticated.value = false;
		}

		return {
			user,
			isLoading,
			login,
			error,
			isAuthenticated,
			is2faPending,
			verify2fa,
			logout,
			setupTwoFactor,
			verify2faAndEnable,
			clearUserValues,
		};
	},
	{
		persist: true,
	},
);
